/**
 * v2 Excel 解析（纯函数，输入二维数组，前端 SheetJS sheet_to_json(header:1) 后调用）。
 *  - parseBomFlatSheet：S1 Ultra.xls 格式 → 扁平 BOM 行（不建树）
 *  - parseEcnSheet：ECN20260122B.xls 格式 → 表头 + 变更前后明细
 */
function num(v, dflt = 0) {
    if (typeof v === 'number')
        return v;
    const t = String(v ?? '').trim();
    if (t === '')
        return dflt; // 空单元格：Number('') 会得 0，须走回退值（ECN 序号回退到顺序号）
    const n = Number(t);
    return Number.isFinite(n) ? n : dflt;
}
function str(v) {
    return String(v ?? '').trim();
}
// ============================================================
// BOM（S1 Ultra.xls）：第 1 行表头，列名见下
// ============================================================
/** 源列名 → 内部字段 */
const BOM_COL_MAP = {
    子件编号: 'part_no',
    物料名称: 'name',
    规格: 'spec',
    单位: 'unit',
    单位用量: 'qty',
    位号: 'designator',
    状态: 'change_status',
    ECN编号: 'ecn_no',
    层级: 'level_label',
    BOM备注: 'note',
    BOM序号: 'seq',
};
/** 解析扁平 BOM。rows[0] 为表头行。 */
export function parseBomFlatSheet(rows) {
    const result = { rows: [], errors: [] };
    if (rows.length < 2) {
        result.errors.push({ rowIndex: -1, reason: '空表或无数据行' });
        return result;
    }
    const header = (rows[0] ?? []).map((c) => str(c));
    const partNoIdx = header.findIndex((h) => h === '子件编号');
    if (partNoIdx < 0) {
        result.errors.push({ rowIndex: 0, reason: '表头缺「子件编号」列' });
        return result;
    }
    let auto = 0;
    for (let i = 1; i < rows.length; i++) {
        const raw = rows[i] ?? [];
        if (raw.every((c) => str(c) === ''))
            continue; // 跳过全空行
        const partNo = str(raw[partNoIdx]);
        if (!partNo)
            continue; // 无料号的行（分隔/小计）跳过
        auto += 1;
        const row = {
            seq: auto,
            part_no: partNo,
            name: '',
            spec: '',
            unit: 'PCS',
            qty: 1,
            extra: {},
        };
        header.forEach((col, idx) => {
            const val = raw[idx];
            const field = BOM_COL_MAP[col];
            if (field === 'part_no')
                return; // 已处理
            if (field === 'seq') {
                // Excel「BOM序号」可能重复，不能当唯一键；仅留原值供参考，排序/键一律用解析行号
                const sv = str(val);
                if (sv)
                    (row.extra ??= {})['BOM序号'] = sv;
            }
            else if (field === 'qty') {
                row.qty = num(val, 1);
            }
            else if (field) {
                row[field] = str(val);
            }
            else if (col) {
                // 其余源列原样保留
                const sv = str(val);
                if (sv)
                    (row.extra ??= {})[col] = sv;
            }
        });
        if (!row.unit)
            row.unit = 'PCS';
        result.rows.push(row);
    }
    if (result.rows.length === 0)
        result.errors.push({ rowIndex: -1, reason: '无有效物料行' });
    return result;
}
/** 从形如「日期：20260122」「变更PCBA料号：910004-0500」的单元格取值 */
function pickAfterColon(cells, label) {
    for (const c of cells) {
        if (c.startsWith(label))
            return c
                .slice(label.length)
                .replace(/^[:：]/, '')
                .trim();
    }
    return '';
}
/** 取「标签: 值」或「标签」右侧紧邻单元格的值（行内右侧首个非空） */
function labeledValue(flat, label) {
    for (const row of flat) {
        for (let c = 0; c < row.length; c++) {
            const cell = row[c] ?? '';
            const idx = cell.indexOf(label);
            if (idx < 0)
                continue;
            const after = cell
                .slice(idx + label.length)
                .replace(/^[:：]/, '')
                .trim();
            if (after)
                return after;
            for (let k = c + 1; k < row.length; k++)
                if (str(row[k]))
                    return str(row[k]);
        }
    }
    return '';
}
/** 日期归一化为 YYYYMMDD：支持「2026年3月10日」「20260122」及 Excel 序列号（如 46091） */
function toYmd(raw) {
    const digits = raw.replace(/[^0-9]/g, '');
    if (digits.length === 8)
        return digits;
    if (digits.length >= 4 && digits.length <= 6) {
        const n = Number(digits);
        if (n > 20000 && n < 90000) {
            // Excel 序列号 → 日期（1899-12-30 起算；25569 = 到 1970-01-01 的天数）
            const d = new Date(Math.round((n - 25569) * 86400000));
            return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}`;
        }
    }
    return digits.slice(0, 8);
}
const EMPTY_ITEM = { part_no: '', name: '', spec: '', designator: '', qty: '' };
/** 横向布局（ECN20260122B）：一行内「变更前块 | 变更后块」并排 */
function parseHorizontal(flat, headerIdx, res) {
    const hRow = flat[headerIdx] ?? [];
    const beforeCol = hRow.indexOf('变更前');
    const afterCol = hRow.indexOf('变更后');
    const pick = (raw, base) => ({
        part_no: str(raw[base]),
        name: str(raw[base + 1]),
        spec: str(raw[base + 2]),
        designator: str(raw[base + 3]),
        qty: str(raw[base + 4]),
    });
    for (let i = headerIdx + 2; i < flat.length; i++) {
        const raw = flat[i] ?? [];
        if (raw.every((c) => c === ''))
            continue;
        const before = pick(raw, beforeCol);
        const after = pick(raw, afterCol);
        if (!before.part_no && !after.part_no)
            continue;
        const no = num(raw[0], res.lines.length + 1);
        const remark = str(raw[raw.length - 1]);
        res.lines.push({ no, before, after, ...(remark && remark !== after.qty ? { remark } : {}) });
    }
}
/** 纵向布局（豪泰 I9）：每个 NO. 两行，D 列标「前」「后」，共用后续列 */
function parseVertical(flat, headerIdx, res) {
    const h = flat[headerIdx] ?? [];
    const col = (label) => h.findIndex((c) => c.includes(label));
    const noCol = col('NO') >= 0 ? col('NO') : 0;
    const changeCol = col('变更'); // 前/后 标记列
    const partCol = col('子项料号');
    const nameCol = col('材料名称');
    const specCol = col('规格描述');
    const qtyCol = col('用量');
    const noteCol = col('变更说明');
    if (partCol < 0 && specCol < 0) {
        res.errors.push({ rowIndex: headerIdx, reason: '纵向表头缺「子项料号/规格描述」列' });
        return;
    }
    const pick = (row) => ({
        part_no: partCol >= 0 ? str(row[partCol]) : '',
        name: nameCol >= 0 ? str(row[nameCol]) : '',
        spec: specCol >= 0 ? str(row[specCol]) : '',
        designator: '',
        qty: qtyCol >= 0 ? str(row[qtyCol]) : '',
    });
    let cur = null;
    let seq = 0;
    for (let i = headerIdx + 1; i < flat.length; i++) {
        const row = flat[i] ?? [];
        if (row.every((c) => c === ''))
            continue;
        const mark = changeCol >= 0 ? str(row[changeCol]) : '';
        if (mark.includes('前')) {
            cur = {
                no: num(row[noCol], ++seq),
                before: pick(row),
                note: noteCol >= 0 ? str(row[noteCol]) : '',
            };
        }
        else if (mark.includes('后')) {
            const after = pick(row);
            const before = cur?.before ?? { ...EMPTY_ITEM };
            const note = cur?.note || (noteCol >= 0 ? str(row[noteCol]) : '');
            if (before.part_no || after.part_no) {
                res.lines.push({
                    no: cur?.no ?? ++seq,
                    before,
                    after,
                    ...(note ? { remark: note } : {}),
                });
            }
            cur = null;
        }
    }
}
/** 解析 ECN。自动识别横向（变更前/后并排）与纵向（前/后分行，豪泰 I9）两种格式。 */
export function parseEcnSheet(rows) {
    const res = {
        ecn_date: '',
        change_subject: '',
        affected_pcba: '',
        lines: [],
        errors: [],
    };
    const flat = rows.map((r) => r.map((c) => str(c)));
    const allCells = flat.flat();
    res.ecn_date = toYmd(labeledValue(flat, '日期'));
    res.affected_pcba = pickAfterColon(allCells, '变更PCBA料号');
    res.change_subject = pickAfterColon(allCells, '变更项目');
    // 布局判定
    const horizIdx = flat.findIndex((r) => r.includes('变更前') && r.includes('变更后'));
    if (horizIdx >= 0) {
        parseHorizontal(flat, horizIdx, res);
    }
    else {
        const vertIdx = flat.findIndex((r) => r.some((c) => c.includes('变更')) &&
            r.some((c) => c.includes('子项料号') || c.includes('规格描述')));
        if (vertIdx >= 0)
            parseVertical(flat, vertIdx, res);
        else
            res.errors.push({
                rowIndex: -1,
                reason: '无法识别 ECN 表头（需含「变更前/变更后」或「变更 + 子项料号」）',
            });
    }
    // 变更料号缺省时，用首行子项料号兜底
    if (!res.affected_pcba && res.lines[0]) {
        res.affected_pcba = res.lines[0].before.part_no || res.lines[0].after.part_no;
    }
    if (res.lines.length === 0 && res.errors.length === 0) {
        res.errors.push({ rowIndex: -1, reason: '无有效变更明细行' });
    }
    return res;
}
//# sourceMappingURL=excel-parser-v2.js.map